import type { IReport } from '../report.interface';
import type { IReportingMetadata } from '../hooks/useReportingMetadata';
import { getNumericSortCompare } from './gridQuery.adapters';
import {
  eventColumnKeyFromEventName,
  eventNameFromMatchedEventMetricKey,
  eventNameFromMetricKey,
  eventNameFromSumEventMetricKey,
  FREQUENCY_FORMAT,
  IMPRESSION_FREQUENCY_METRIC,
  REACH_FORMAT,
  REACH_METRIC,
  isEventMetricKey,
  isMatchedEventMetricKey,
  isPredictedMetricKey,
  isSumEventMetricKey,
  matchedEventColumnKeyFromEventName,
  eventNameFromPredictedMetricKey,
  predictedColumnKeyFromEventName,
  predictedErrorColumnKeyFromEventName,
  predictedRemainingColumnKeyFromEventName,
  sumEventColumnKeyFromEventName,
  type IColumnFormatSpec,
} from '../reports';

export interface IReportColumnDef {
  name: string;
  label: string;
  options: { filter?: boolean; sort?: boolean; sortCompare?: unknown };
  format?: IColumnFormatSpec;
  /** Predicted-error percentage, coloured by band rather than shown as a bare number. */
  isErrorPct?: boolean;
}

/**
 * Metric-picker key -> projected column key in the response row.
 *
 * Constraints must be expressed in these column keys, so they resolve through here too:
 * a key derived any other way can name a column the backend does not project, which is
 * a 400 rather than a filtered report.
 */
export const metricColumnName = (eventType: string): string => {
  if (isEventMetricKey(eventType)) {
    return eventColumnKeyFromEventName(eventNameFromMetricKey(eventType));
  }
  if (isMatchedEventMetricKey(eventType)) {
    return matchedEventColumnKeyFromEventName(eventNameFromMatchedEventMetricKey(eventType));
  }
  if (isSumEventMetricKey(eventType)) {
    return sumEventColumnKeyFromEventName(eventNameFromSumEventMetricKey(eventType));
  }
  return eventType;
};

// Longest prefix first: 'event_' would otherwise match a matched_event_ column.
const EVENT_COLUMN_FLAVOURS: Array<{
  prefix: string;
  isKind: (metricKey: string) => boolean;
  columnKey: (metricKey: string) => string;
}> = [
  {
    prefix: 'matched_event_',
    isKind: isMatchedEventMetricKey,
    columnKey: (k) => matchedEventColumnKeyFromEventName(eventNameFromMatchedEventMetricKey(k)),
  },
  {
    prefix: 'sum_event_',
    isKind: isSumEventMetricKey,
    columnKey: (k) => sumEventColumnKeyFromEventName(eventNameFromSumEventMetricKey(k)),
  },
  {
    prefix: 'event_',
    isKind: isEventMetricKey,
    columnKey: (k) => eventColumnKeyFromEventName(eventNameFromMetricKey(k)),
  },
];

const eventColumnLabel = (column: string, report: IReport): string | null => {
  const flavour = EVENT_COLUMN_FLAVOURS.find((f) => column.startsWith(f.prefix));
  if (!flavour) return null;
  const selected = report.metrics.find(
    (m) => flavour.isKind(m.eventType) && flavour.columnKey(m.eventType) === column
  );
  return selected?.label || column.slice(flavour.prefix.length);
};

// Longest prefix first: 'predicted_' would otherwise swallow a predicted_remaining_ column.
const predictedColumnLabel = (column: string, report: IReport): string | null => {
  if (!column.startsWith('predicted_')) return null;

  for (const metric of report.metrics) {
    if (!isPredictedMetricKey(metric.eventType)) continue;
    const event = eventNameFromPredictedMetricKey(metric.eventType);
    // The metric's own label already reads "Predicted <event>".
    if (predictedColumnKeyFromEventName(event) === column) return metric.label;
    if (predictedErrorColumnKeyFromEventName(event) === column) {
      return `${metric.label} ± %`;
    }
  }
  return null;
};

export const buildReportColumns = (
  data: any,
  report: IReport,
  metadata: IReportingMetadata,
  calcFormats?: Record<string, IColumnFormatSpec>
): IReportColumnDef[] => {
  if (!data) return [];

  const cols: IReportColumnDef[] = [];
  const customLabelMap = new Map(
    (report.customLabels || []).map((item) => [item.key, item.value])
  );

  const numericKeys = new Set<string>([
    ...metadata.metrics.map((m) => m.key),
    ...metadata.dimensions.filter((d) => d.type === 'integer').map((d) => d.key),
  ]);
  const dateKeys = new Set(
    metadata.dimensions.filter((d) => d.type === 'date').map((d) => d.key)
  );
  const dimensionKeys = new Set(metadata.dimensions.map((d) => d.key));

  for (const column of Object.keys(data)) {
    const options: IReportColumnDef['options'] = { filter: true, sort: true };
    // event_date shares the event-metric prefix; unguarded it labels itself 'date'.
    const isDimension = dimensionKeys.has(column);
    const eventLabel = isDimension ? null : eventColumnLabel(column, report);
    const predictedLabel = isDimension ? null : predictedColumnLabel(column, report);
    const columnDefinition: IReportColumnDef = {
      name: column,
      label:
        eventLabel ||
        predictedLabel ||
        customLabelMap.get(column) ||
        metadata.labels.get(column) ||
        column,
      options,
    };

    if (calcFormats && calcFormats[column]) {
      columnDefinition.format = calcFormats[column];
      options.sortCompare = getNumericSortCompare();
      options.filter = false;
    } else if (column === REACH_METRIC) {
      columnDefinition.format = REACH_FORMAT;
      options.sortCompare = getNumericSortCompare();
    } else if (column === IMPRESSION_FREQUENCY_METRIC) {
      columnDefinition.format = FREQUENCY_FORMAT;
      options.sortCompare = getNumericSortCompare();
    }

    const sampleValue = data[column];
    const sniffedNumeric =
      typeof sampleValue === 'number' ||
      (typeof sampleValue === 'string' &&
        sampleValue.trim() !== '' &&
        !Number.isNaN(Number.parseFloat(sampleValue)) &&
        !/[%:/-]/.test(sampleValue));
    if (numericKeys.has(column) || sniffedNumeric || eventLabel !== null || predictedLabel !== null) {
      options.sortCompare = getNumericSortCompare();
    }

    // Marked rather than sniffed by name: the grid colours it by band, and matching on a prefix there
    // would catch any future column that happens to end in a percentage.
    if (!isDimension && column.startsWith('predicted_error_pct_')) {
      columnDefinition.isErrorPct = true;
      // Not sortable, unlike the total beside it. The total is materialised into
      // conversion_prediction and projected under its own alias, so ORDER BY can name it; the margin
      // of error is derived during the merge and does not exist in SQL. Offering the header control
      // gave a sort arrow that quietly reordered nothing.
      options.sort = false;
    }

    if (dateKeys.has(column)) {
      cols.unshift(columnDefinition);
    } else {
      cols.push(columnDefinition);
    }
  }

  // Predicted keys are listed explicitly because metricColumnName leaves them untranslated, so the
  // columns they produce would otherwise stay filterable. A filter names a dimension, which no
  // prediction is, and the backend rejects it outright.
  //
  // Sorting is a different matter and is left on: the estimate is materialised into
  // conversion_prediction and projected under its own alias, so ORDER BY can name it and the ordering
  // is the whole report's rather than the page's.
  const predictedColumnNames = report.metrics
    .filter((m) => isPredictedMetricKey(m.eventType))
    .flatMap((m) => {
      const event = eventNameFromPredictedMetricKey(m.eventType);
      return [
        predictedColumnKeyFromEventName(event),
        predictedRemainingColumnKeyFromEventName(event),
        predictedErrorColumnKeyFromEventName(event),
      ];
    });
  const metricColumnNames = new Set<string>([
    ...metadata.metrics.map((m) => m.key),
    ...report.metrics.map((m) => metricColumnName(m.eventType)),
    ...predictedColumnNames,
  ]);
  for (const col of cols) {
    if (metricColumnNames.has(col.name)) col.options.filter = false;
  }

  const colByName = new Map<string, IReportColumnDef>(cols.map((col) => [col.name, col]));
  const orderedNames: string[] = [];
  const pushName = (name: string) => {
    if (colByName.has(name) && !orderedNames.includes(name)) orderedNames.push(name);
  };

  // Backend grouping order: any auto-added date leads, then the user's picks.
  for (const name of Object.keys(data)) {
    if (dimensionKeys.has(name)) pushName(name);
  }
  for (const dimension of report.dimensions) pushName(dimension.key);
  for (const metric of report.metrics) pushName(metricColumnName(metric.eventType));
  for (const col of cols) pushName(col.name);

  return orderedNames.map((name) => colByName.get(name)!);
};
